import type React from 'react';
import type { CurrencyApiRow } from '@/api/reference-admin';
import { currencyEntityConfig } from '@/config/reference-admin-entities';
import { formatDate } from '@/lib/utils';
import {
  type ColumnConfig,
  DataTable,
  type DataTableResponse,
  type FilterConfig,
} from './data-table';

import { DeleteReferenceAdminDialog } from './modals/delete-reference-admin-dialog';
import { EditReferenceAdminModal } from './modals/edit-reference-admin-modal';
import { ReferenceAdminStatus, StatusChip } from './status-chip';

// Export the CurrencyApiRow type as CurrencyRow for consistency
export type CurrencyRow = CurrencyApiRow;

interface Props {
  data?: DataTableResponse<CurrencyApiRow> | undefined;
  isLoading?: boolean;
  error?: string | null;
  onFilterChange?: (filterKey: string, value: string | string[]) => void;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  onPageChange?: (page: number) => void;
  onRefresh?: () => void;
  headerActions?: React.ReactNode;
}

export const CurrenciesTable: React.FC<Props> = ({
  data,
  isLoading = false,
  error = null,
  onFilterChange,
  onSortChange,
  onPageChange,
  onRefresh,
  headerActions,
}) => {
  const columns: ColumnConfig<CurrencyApiRow>[] = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (item) => item.code,
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (item) => item.name,
    },
    {
      key: 'symbol',
      label: 'Symbol',
      sortable: false,
      render: (item) => item.symbol || '—',
    },
    {
      key: 'decimal_places',
      label: 'Decimals',
      sortable: true,
      align: 'center',
      render: (item) => item.decimal_places.toString(),
    },
    {
      key: 'is_crypto',
      label: 'Crypto',
      sortable: true,
      align: 'center',
      render: (item) => (item.is_crypto ? '✓' : '—'),
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      align: 'center',
      render: (item) => (
        <StatusChip
          type="reference"
          status={item.is_active ? ReferenceAdminStatus.ACTIVE : ReferenceAdminStatus.INACTIVE}
        />
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      align: 'right',
      render: (item) => (
        <span className="text-muted-foreground">
          {item.created_at ? formatDate(item.created_at) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      align: 'center',
      render: (row) => (
        <div className="flex items-center gap-1 justify-center">
          <EditReferenceAdminModal
            config={currencyEntityConfig}
            entity={row}
            onSuccess={onRefresh}
          />
          <DeleteReferenceAdminDialog
            config={currencyEntityConfig}
            entity={row}
            onSuccess={onRefresh}
          />
        </div>
      ),
    },
  ];

  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      placeholder: 'Select Status',
      multiSelect: false,
      options: [
        { value: ReferenceAdminStatus.ACTIVE, label: 'Active' },
        { value: ReferenceAdminStatus.INACTIVE, label: 'Inactive' },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      <DataTable
        data={data}
        columns={columns}
        caption="Currencies"
        rowHeaderColumnKey="name"
        filters={filters}
        headerActions={headerActions}
        isLoading={isLoading}
        error={error}
        {...(onFilterChange && { onFilterChange })}
        {...(onSortChange && { onSortChange })}
        {...(onPageChange && { onPageChange })}
        emptyMessage="No currencies found"
        loadingMessage="Loading currencies..."
        defaultSortKey="name"
        defaultSortOrder="asc"
        compact={true}
      />
    </div>
  );
};
